/**
 * Display helpers for block-owned / template regions.
 * Omits empty defaults (no "Landmarks: None" / "Damage: No" noise).
 */

const HEMISPHERE_SHORT = {
    left: 'L',
    right: 'R',
    Left: 'L',
    Right: 'R',
    L: 'L',
    R: 'R',
};

export function regionDisplayName(region) {
    if (!region) return '';
    return region.displayName || region.abbreviation || region.name || region._id || '';
}

export function formatHemisphereShort(hemisphere) {
    if (!hemisphere) return null;
    return HEMISPHERE_SHORT[hemisphere] || String(hemisphere);
}

/**
 * Short chip label parts (non-empty only).
 * e.g. ["Amygdala", "L", "coronal"] or ["Amygdala", "landmarks: x"]
 */
export function formatRegionChipParts(region) {
    const parts = [];
    const name = regionDisplayName(region);
    if (name) parts.push(name);

    const details = region?.details || {};
    const hemi = formatHemisphereShort(details.hemisphere);
    if (hemi) parts.push(hemi);

    const orientation = details.sliceOrientation;
    if (orientation) parts.push(String(orientation));

    const landmarks = Array.isArray(details.landmarks) ? details.landmarks.filter(Boolean) : [];
    if (landmarks.length) parts.push(landmarks.join(', '));

    const damage = Array.isArray(details.damage) ? details.damage.filter(Boolean) : [];
    if (damage.length) parts.push(`Damage: ${damage.join(', ')}`);

    return parts;
}

/** Single-line chip label joined with · */
export function formatRegionChipLabel(region) {
    return formatRegionChipParts(region).join(' · ');
}

/** Tooltip with fuller detail (still omits empty defaults). */
export function formatRegionTooltip(region) {
    const details = region?.details || {};
    const lines = [regionDisplayName(region)];
    if (details.hemisphere) lines.push(`Hemisphere: ${details.hemisphere}`);
    if (details.sliceOrientation) lines.push(`Orientation: ${details.sliceOrientation}`);
    const landmarks = Array.isArray(details.landmarks) ? details.landmarks.filter(Boolean) : [];
    if (landmarks.length) lines.push(`Landmarks: ${landmarks.join(', ')}`);
    const damage = Array.isArray(details.damage) ? details.damage.filter(Boolean) : [];
    if (damage.length) lines.push(`Damage: ${damage.join(', ')}`);
    return lines.filter(Boolean).join('\n');
}

/**
 * Compact one-line summary for dense block rows.
 * Shows up to `limit` region names, then "+N".
 * @returns {{ text: string, full: string, count: number }}
 */
export function formatBlockRegionSummary(regions, limit = 3) {
    const list = Array.isArray(regions) ? regions : [];
    const names = list.map((r) => regionDisplayName(r)).filter(Boolean);
    const count = names.length;
    if (count === 0) {
        return { text: 'No regions', full: 'No regions', count: 0 };
    }
    const shown = names.slice(0, Math.max(0, limit));
    const remaining = count - shown.length;
    const text = remaining > 0
        ? `${shown.join(' · ')} · +${remaining}`
        : shown.join(' · ');
    return { text, full: names.join(' · '), count };
}
